import React, { useEffect, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LayersIcon from "@mui/icons-material/Layers";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkIcon from "@mui/icons-material/Work";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Button,
  useTheme,
  alpha,
  Tooltip,
  IconButton,
  Skeleton,
  Card,
} from "@mui/material";

import { OrganizationEndpoints } from "@generated/organization";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { TagChips } from "src/shell/components/TagChips";
import { getCardStyles } from "src/shell/styles/themeUtils";

interface Organization {
  id: string | number;
  name: string;
  alias: string;
  description?: string;
  tags?: Record<string, string>;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

interface OrgSummary {
  memberCount: number;
  projectCount: number;
  systemCount: number;
  runningCount: number;
  failedCount: number;
}

interface OrganizationHeaderProps {
  organization: Organization;
  onEditOrganization?: () => void;
  compact?: boolean;
}

interface StatTileProps {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color: "primary" | "success" | "error" | "warning" | "secondary" | "info";
  loading?: boolean;
  trend?: { direction: "up" | "down"; value: string };
}

function StatTile({
  label,
  value,
  icon,
  color,
  loading,
  trend,
}: StatTileProps) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        textAlign: "center",
        p: 2,
        borderRadius: 1,
        bgcolor: alpha(theme.palette[color].main, 0.08),
        transition: "background 150ms ease",
        "&:hover": {
          bgcolor: alpha(theme.palette[color].main, 0.12),
        },
      }}
    >
      <Stack alignItems="center" spacing={0.5}>
        <Box sx={{ color: `${color}.main` }}>{icon}</Box>
        {loading ? (
          <Skeleton width={32} height={32} />
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="h5" color={`${color}.main`} fontWeight={700}>
              {value}
            </Typography>
            {trend && (
              <Stack
                direction="row"
                spacing={0.25}
                alignItems="center"
                sx={{
                  color:
                    trend.direction === "up" ? "success.main" : "error.main",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                {trend.direction === "up" ? (
                  <TrendingUpIcon sx={{ fontSize: 14 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 14 }} />
                )}
                {trend.value}
              </Stack>
            )}
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
    </Box>
  );
}

const OrganizationHeader: React.FC<OrganizationHeaderProps> = ({
  organization,
  onEditOrganization,
  compact = false,
}) => {
  const theme = useTheme();
  const [summary, setSummary] = useState<OrgSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    if (!organization?.alias) return;
    setSummaryLoading(true);
    const url =
      apiBaseUrls.BACKEND +
      OrganizationEndpoints.GET_ORGANIZATION_SUMMARY.replace(
        ":id",
        organization.alias,
      );
    const token = orgSessionManager.getToken() ?? "";
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => setSummary(data))
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [organization?.alias]);

  return (
    <Card sx={{ ...getCardStyles(theme), p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
              >
                {organization.name}
              </Typography>
              <Tooltip title="Organization unique identifier" arrow>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{
                    fontWeight: 500,
                    fontFamily: "monospace",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    ml: 1,
                    mb: 1,
                  }}
                >
                  {organization.alias}
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ opacity: 0.6, fontSize: 16 }}
                  />
                </Typography>
              </Tooltip>
            </Stack>

            {organization.description && !compact && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 1, mb: 2, maxWidth: "800px" }}
              >
                {organization.description}
              </Typography>
            )}

            {!compact && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={4}>
                  <StatTile
                    label="Members"
                    value={summary?.memberCount ?? "—"}
                    icon={<PeopleIcon fontSize="small" />}
                    color="primary"
                    loading={summaryLoading}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <StatTile
                    label="Projects"
                    value={summary?.projectCount ?? "—"}
                    icon={<WorkIcon fontSize="small" />}
                    color="success"
                    loading={summaryLoading}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <StatTile
                    label="Stacks"
                    value={summary?.systemCount ?? "—"}
                    icon={<LayersIcon fontSize="small" />}
                    color="info"
                    loading={summaryLoading}
                  />
                </Grid>
              </Grid>
            )}

            {organization.tags && Object.keys(organization.tags).length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <TagChips tags={organization.tags} />
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: { xs: "flex-start", md: "flex-end" },
              position: "relative",
              zIndex: 1,
              pt: { xs: 0, md: 1 },
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SettingsIcon />}
                onClick={onEditOrganization}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  px: 2,
                  borderWidth: 1,
                  "&:hover": { borderWidth: 1 },
                  "&:active": { transform: "scale(0.98)" },
                  transition: "all 150ms ease",
                }}
              >
                Edit Organization
              </Button>
              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  aria-label="More actions"
                  sx={{
                    borderRadius: 1,
                    transition: "background 150ms ease",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack spacing={0.75} sx={{ width: "100%" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ScheduleIcon
                  fontSize="small"
                  sx={{ color: "text.disabled", fontSize: 16 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Created:{" "}
                  {new Date(organization.createdAt).toLocaleDateString()}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <ScheduleIcon
                  fontSize="small"
                  sx={{ color: "text.disabled", fontSize: 16 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Updated:{" "}
                  {new Date(organization.updatedAt).toLocaleDateString()}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default OrganizationHeader;
